import { EnumUserRoles } from 'entities/Users/model/types'
import { NAV_AREAS, NAV_AREA_ROUTES, type NavArea } from 'entities/WebMcp/model/navAreas'
import { type ScopedStarter } from 'entities/WebMcp/model/starters'

export type AreaCapability = {
  tools: string[]
  suggestions: ScopedStarter[]
}

// MEMBER is what the empty state's page tier already required, so no suggestion
// becomes newly visible or hidden by gaining a role of its own.
const page = (key: string, label: string): ScopedStarter => ({
  key,
  label,
  prompt: label,
  minRole: EnumUserRoles.MEMBER,
})

export const ROUTE_CAPABILITIES: Record<NavArea, AreaCapability> = {
  Inboxes: {
    tools: [
      'navigate',
      'search_contacts',
      'get_contact',
      'list_inboxes',
      'send_message',
      'list_unread_conversations',
      'search_conversations',
      'open_conversation_with_contact',
      'open_conversation',
      'filter_conversations_list',
      'reply_to_conversation',
    ],
    suggestions: [
      page('find_conversation', 'Find a conversation'),
      page('filter_conversations', 'Filter my conversations'),
      page('find_contact_to_message', 'Find a contact to message'),
    ],
  },
  // Nav-only areas (no page-specific tools yet) carry no page-tier suggestions, so
  // the empty-state ladder falls through to the curated discovery tier rather than
  // offering bare "go elsewhere" pills. Add real suggestions when the area gets tools.
  Discover: {
    tools: ['navigate'],
    suggestions: [],
  },
  'AI & Automation': {
    tools: ['navigate'],
    suggestions: [],
  },
  Outbound: {
    tools: [
      'navigate',
      'search_contacts',
      'list_tags',
      'list_broadcast_numbers',
      'open_broadcast_create',
      'set_broadcast_details',
      'set_broadcast_audience',
      'set_broadcast_sender',
    ],
    suggestions: [
      page('draft_broadcast_to_tag', 'Draft a broadcast to a tag'),
      page('broadcast_from_inbox', 'Create a broadcast from an inbox'),
    ],
  },
  Contacts: {
    tools: [
      'navigate',
      'search_contacts_list',
      'search_contacts',
      'get_contact',
      'open_contact',
      'list_tags',
      'create_tag',
      'tag_contact',
      'create_contact',
      'update_contact',
    ],
    suggestions: [
      page('search_contacts_by_name', 'Search contacts by name'),
      page('open_a_contact', 'Open a contact'),
    ],
  },
  // query_analytics unregistered (SWC-19442): analytics is out of the navigate-first
  // P0 scope, so Analytics is nav-only and its suggestions fall through to discovery.
  Analytics: {
    tools: ['navigate'],
    suggestions: [],
  },
}

/** Resolve the current main area from a pathname by matching NAV_AREA_ROUTES prefixes. */
export const resolveArea = (pathname: string): NavArea | null => {
  const trimmed = pathname.replace(/^\/+/, '')
  for (const area of NAV_AREAS) {
    const root = NAV_AREA_ROUTES[area]
    if (trimmed === root || trimmed.startsWith(`${root}/`)) return area
  }
  return null
}
